// Matrix — full course content for the DSA Explanation Modal.
// Java-focused. Same section-course schema as mathBit.js: concept → internals →
// applied templates → errors → complexity → beginner drills → cheatsheet → playground.
// Reuses the existing 'grid-diagram' section type (introduced for 2-D DP) for its visual.

import matrixDrills from './matrixDrills';
const drill = id => matrixDrills.find(d => d.id === id);

const matrix = {
  key: 'Matrix',
  tagline: 'Traverse a grid without an extra visited array, and transform it in place without a second copy.',

  sections: [
    {
      id: 'intro',
      title: 'Introduction to Matrix Problems',
      type: 'text',
      body: [
        "Matrix problems work on 2-D grids, but the actual skill they test is rarely about the grid itself — it's about tracking POSITION and BOUNDARY cleanly enough to traverse or transform every cell exactly once, usually without allocating a second grid to help.",
        "Two families of technique cover almost everything in this pattern: traversal (visiting cells in some specific order — spiral, diagonal, row-major binary search) and in-place transformation (rotating, zeroing, updating a grid using only the memory the input already occupies).",
      ],
    },
    {
      id: 'why',
      title: 'Why the Matrix Pattern?',
      type: 'text',
      body: [
        "A matrix is really just a 1-D array wearing a 2-D costume — every technique from arrays and two pointers still applies, once row/column indices are tracked correctly. What makes matrix problems feel distinct is the BOOKKEEPING: which boundary has been fully visited, which direction to turn next, which cell's original value still needs to be read before it gets overwritten.",
        "The reason in-place solutions matter so much here specifically is that a full second grid is O(rows × cols) extra memory — often the single largest cost in the whole solution. Interviewers frequently follow up an easy \"use extra space\" answer by asking for the O(1)-space version, so recognizing the in-place trick (reuse the first row as markers, transpose-then-reverse instead of building a new grid) is the core skill this pattern trains.",
      ],
    },
    {
      id: 'analogy',
      title: 'How the Matrix Pattern Works — Two Real-World Pictures',
      type: 'text',
      heading: 'A Snail Circling Inward, and Folding a Photograph',
      body: [
        "Picture a snail starting at the top-left corner of a garden bed, always crawling forward until it hits the edge of the bed or a part it's already crossed, then turning. It never needs a map marking where it's already been — the SHRINKING boundary of unexplored garden is enough to know when to turn. That's exactly how spiral traversal works: four shrinking boundaries (top, bottom, left, right) replace the need for a separate visited grid entirely.",
        "Now picture rotating a photograph 90 degrees by hand, without a spare copy to work from: first flip it along its diagonal (top-left corner to bottom-right corner stays fixed, everything else swaps across that line — this is a transpose), then flip each row left-to-right. Two simple, well-known folds, done directly on the original photo, combine into a full rotation — no second photograph ever needed.",
      ],
    },
    {
      id: 'visualize',
      title: 'Visualizing a Spiral Traversal',
      type: 'grid-diagram',
      body: [
        "Four shrinking boundaries — top, bottom, left, right — are all that's needed to read a matrix in spiral order. After each full sweep along one edge, that boundary moves one step inward, and the next sweep begins.",
        "This is the completed 3×3 spiral order: 1, 2, 3, 6, 9, 8, 7, 4, 5 — read by sweeping the top row, then the right column, then the bottom row, then the left column, with the single center cell read last.",
      ],
      demo: {
        grid: [[1, 2, 3], [4, 5, 6], [7, 8, 9]],
        highlightIndices: [[1, 1]],
        note: 'The center cell (highlighted) is read last, once every boundary has shrunk down to meet it.',
      },
    },
    {
      id: 'template',
      title: 'The Core Matrix Vocabulary',
      type: 'code',
      code:
`// Boundary tracking — replaces a visited grid for many traversal problems:
int top = 0, bottom = rows - 1, left = 0, right = cols - 1;

// Converting a flat index to (row, col), and back — the key to treating
// a sorted matrix as a single flattened 1-D array for binary search:
int row = flatIndex / cols, col = flatIndex % cols;
int flatIndex = row * cols + col;

// The two in-place transformation building blocks:
int temp = matrix[i][j]; matrix[i][j] = matrix[j][i]; matrix[j][i] = temp;   // transpose swap
// ...then reverse each row (or column) to complete a rotation.`,
      note: 'Nearly every problem in this pattern combines these four ideas: track boundaries instead of a visited set, convert between flat and 2-D indices when treating the grid as a single sequence, and compose transpose + reverse for in-place rotations.',
    },
    {
      id: 'spiraltemplate',
      title: 'Applied: Boundary-Shrinking Traversal',
      type: 'code',
      code:
`List<Integer> spiralOrder(int[][] matrix) {
    int top = 0, bottom = matrix.length - 1;
    int left = 0, right = matrix[0].length - 1;
    List<Integer> result = new ArrayList<>();
    while (top <= bottom && left <= right) {
        for (int j = left; j <= right; j++) result.add(matrix[top][j]);
        top++;
        for (int i = top; i <= bottom; i++) result.add(matrix[i][right]);
        right--;
        if (top <= bottom) {
            for (int j = right; j >= left; j--) result.add(matrix[bottom][j]);
            bottom--;
        }
        if (left <= right) {
            for (int i = bottom; i >= top; i--) result.add(matrix[i][left]);
            left++;
        }
    }
    return result;
}`,
      note: 'The two "if" guards matter: without them, a single row or single column remaining mid-spiral would get read TWICE (once going right/down, then incorrectly again going left/up) — always check that a boundary hasn\'t already crossed before sweeping it.',
    },
    {
      id: 'transposetemplate',
      title: 'Applied: Transpose + Reverse (In-Place Rotation)',
      type: 'code',
      code:
`void rotate(int[][] matrix) {
    int n = matrix.length;
    for (int i = 0; i < n; i++) {
        for (int j = i + 1; j < n; j++) {   // only the upper triangle — each swap handles its mirror too
            int temp = matrix[i][j];
            matrix[i][j] = matrix[j][i];
            matrix[j][i] = temp;
        }
    }
    for (int[] row : matrix) {
        reverse(row);   // then flip each row left-to-right
    }
}`,
      note: 'Transpose alone flips the matrix across its main diagonal; reversing each row afterward is what turns that into a full 90° CLOCKWISE rotation. (Reversing each COLUMN instead, before transposing, produces a counter-clockwise rotation — a useful variant to remember.)',
    },
    {
      id: 'errors',
      title: 'Common Errors',
      type: 'errors',
      items: [
        {
          mistake: 'Forgetting the mid-spiral boundary-crossed check',
          why: 'Once a spiral traversal has shrunk down to a single remaining row or column, sweeping BOTH the "bottom row" and "left column" phases unconditionally reads some cells twice — the row/column has already been fully covered by an earlier phase in the same iteration.',
          fix: 'Always guard the third and fourth sweeps of each spiral iteration with if (top <= bottom) and if (left <= right) respectively — skip them once the boundaries have already crossed.',
        },
        {
          mistake: 'Reading from a cell that\'s already been overwritten',
          why: 'In in-place transformations (rotate, transpose), writing a new value into matrix[j][i] before matrix[i][j] has been read from that OTHER position can silently corrupt the source data — the classic in-place bug of destroying an input before it\'s fully consumed.',
          fix: 'Use a temporary variable to hold a value before overwriting its destination, or restructure the operation (like transpose + reverse) so no cell is ever both a source and a destination in the same step without an explicit swap.',
        },
        {
          mistake: 'Scanning the WHOLE first row/column for zero-markers after they\'ve already been used as markers',
          why: 'In Set Matrix Zeroes\' optimized approach, the first row and column are repurposed as marker storage during the main pass — checking whether they ORIGINALLY had a zero must happen BEFORE that repurposing begins, or the check reads corrupted marker data instead of the true original state.',
          fix: 'Record firstRowZero and firstColZero as separate boolean flags in a dedicated first step, before the first row/column\'s cells get overwritten with marker values.',
        },
        {
          mistake: 'Mixing up row-major flat-index math',
          why: 'Converting a flat index back to (row, col) is flatIndex / cols and flatIndex % cols — using rows instead of cols in either formula (an easy typo) silently reads the wrong cell, especially on non-square matrices where the bug wouldn\'t be caught by symmetry.',
          fix: 'Double-check which dimension (rows or cols) belongs in the division vs. the modulo — the divisor is always the number of COLUMNS, since that\'s how many cells make up one full row.',
        },
      ],
    },
    {
      id: 'complexity',
      title: 'Time Complexity',
      type: 'complexity',
      rows: [
        { op: 'Spiral / diagonal traversal', time: 'O(rows · cols)', note: 'Every cell visited exactly once, no visited grid needed.' },
        { op: 'Set Matrix Zeroes (optimized)', time: 'O(rows · cols)', note: 'Two passes, O(1) extra space using the matrix\'s own first row/column.' },
        { op: 'Rotate Image (transpose + reverse)', time: 'O(n²)', note: 'Two O(n²) passes, O(1) extra space.' },
        { op: 'Search a 2D Matrix (binary search)', time: 'O(log(rows · cols))', note: 'Treats the sorted grid as one flattened array.' },
        { op: 'Valid Sudoku (single-pass keys)', time: 'O(1) — fixed 81-cell board', note: 'Row/column/box keys in one shared set, one pass.' },
      ],
    },
    {
      id: 'when',
      title: 'When to Use',
      type: 'text',
      heading: 'Reach for Matrix techniques when you see…',
      body: [
        'A request to visit every cell of a 2-D grid in a SPECIFIC order (spiral, diagonal, boustrophedon/zigzag) — look for a boundary-tracking or direction-bouncing traversal before reaching for a visited array.',
        'A request to transform a grid IN PLACE (rotate, zero out, flip) — look for a composition of simple, well-known operations (transpose, reverse, swap) rather than building a second grid.',
        'A matrix that\'s sorted along both rows and columns — a strong signal that it can be treated as one flattened sorted sequence for binary search.',
        'A grid-based simulation (Game of Life) where the next state depends on neighbors\' CURRENT state — look for a way to encode both old and new state in the same cell to avoid a full second grid.',
        'When stuck on space complexity, ask: can part of the input itself (a border row, a spare bit per cell) be repurposed as scratch space instead of allocating new memory?',
      ],
    },
    {
      id: 'problems',
      title: 'Beginner Problems (Easy → Medium → Advanced)',
      type: 'problems',
      groups: [
        {
          level: 'Easy',
          items: [
            drill('transpose-matrix'),
            drill('diagonal-traverse'),
            drill('spiral-matrix-drill'),
          ],
        },
        {
          level: 'Medium',
          items: [
            drill('set-matrix-zeroes-drill'),
            drill('rotate-image-drill'),
            drill('search-a-2d-matrix'),
          ],
        },
        {
          level: 'Advanced',
          items: [
            drill('spiral-matrix-ii'),
            drill('valid-sudoku'),
            drill('game-of-life'),
          ],
        },
      ],
      note: 'These are foundation drills to build comfort with boundary tracking and in-place transformation before moving into the curated interview problem list for this pattern.',
    },
    {
      id: 'cheatsheet',
      title: 'Cheatsheet & Recap',
      type: 'cheatsheet',
      items: [
        'Four shrinking boundaries (top, bottom, left, right) replace a visited grid for spiral-style traversal — always guard the 3rd and 4th sweeps with a boundary-crossed check.',
        'Transpose (swap across the diagonal) + reverse each row = 90° clockwise rotation — a reusable decomposition, not just a Rotate Image trick.',
        'A sorted matrix (rows AND columns sorted) can be binary-searched as one flattened array: row = mid / cols, col = mid % cols.',
        'The matrix\'s own first row/column can serve as O(1)-space marker storage — just record their ORIGINAL state in separate flags before repurposing them.',
        'For grid simulations where next-state depends on current-state neighbors, encode old and new state in the same cell (extra bits) instead of allocating a full second grid.',
        'Diagonal traversal direction alternates — track a single boolean (or the parity of row+col) rather than storing every diagonal\'s group of cells.',
      ],
    },
    {
      id: 'playground',
      title: 'Interactive Practice & Quiz',
      type: 'playground',
    },
  ],
};

export default matrix;
